import moment from 'moment';
import { createSelector } from 'reselect';

import { ICustomsInvoiceExternalData } from '@packlink/customs-form';
import {
    Address,
    IAddress,
    ICustomsShipmentDetails,
    IVoucher,
    IPrice,
    ShipmentThirdPartyPreferencesProps,
} from '@packlink/packlink-sdk';

import { IAppState } from '@store/reducers';
import { ServicesType } from '@store/reducers/checkout';
import { mapAvailableCollectionDate } from '@utils';
import { ICollectionDateForm, IParcel, IServiceState } from '@types';

import { ITransactionInfo } from '@Checkout/components/OrderTable/TransactionOrderTable';

export const UK_ISO_CODES = ['GB', 'GG', 'IM'];

/**
 * Transforms a collection of ServiceAvailableDate objects into the ones needed by the Collection form
 * (ICollectionDateForm)
 */
const getAvailableCollectionDates = (state: IAppState): Array<ICollectionDateForm | null> => {
    if (!state.checkout.service) {
        return [];
    }

    const items = state.checkout.service.availableDates.slice(0, 4);
    return items.map(mapAvailableCollectionDate);
};

const getCollectionDate = (state: IAppState): moment.Moment | null => {
    if (!state.checkout.collection?.date) {
        return null;
    }

    return state.checkout.collection.date;
};

const getCollectionInterval = (state: IAppState): string | null => {
    if (!state.checkout.collection?.interval) {
        return null;
    }

    return state.checkout.collection.interval;
};

export const getPriceDetails = (state: IAppState): IPrice | undefined => state.checkout.priceDetails;
export const getCheckoutVoucher = (state: IAppState): IVoucher | null => state.checkout.voucher;

export const getAvailableDatesState = createSelector(
    [getAvailableCollectionDates],
    (availableDates: Array<ICollectionDateForm | null>) => availableDates,
);

export const getCollectionDateState = createSelector([getCollectionDate], (collectionDate) => collectionDate);

export const getCollectionIntervalState = createSelector(
    [getCollectionInterval],
    (collectionInterval) => collectionInterval,
);

export const getShipmentReference = (state: IAppState): string =>
    state.checkout.reference || 'noShipmentReferenceFound';

export const getToAddress = createSelector([(state: IAppState): IAddress | null => state.checkout.to], (to) =>
    Address.deserialize<IAddress>(to),
);

export const getFromAddress = createSelector([(state: IAppState): IAddress | null => state.checkout.from], (from) =>
    Address.deserialize<IAddress>(from),
);

export const getCarriersTermsAndConditions = createSelector(
    [(state: IAppState): boolean | undefined => state.checkout.carriersTermsAndConditions],
    (carriersTermsAndConditions) => !!carriersTermsAndConditions,
);

export const getService = createSelector([(state: IAppState) => state.checkout.service], (service) => service);

export const getServiceHasInsurance = createSelector([getService], (service): boolean => !!service?.insurance);

export const getShowAdditionalInsurance = createSelector(
    [getService],
    (service): boolean | undefined => service?.insurance?.additionalInsurance,
);

export const getServiceCarrier = createSelector(
    [getService],
    (service: IServiceState | null): string | undefined => service?.carrier,
);

export const getServiceName = createSelector(
    [getService],
    (service: IServiceState | null): string | undefined => service?.name,
);

export const getCustomsInvoiceId = createSelector(
    [getService],
    (service): string | undefined => service?.customsInvoiceId,
);

export const getIsCustomsRequired = createSelector([getService], (service) => service?.customsRequired ?? false);

export const getUrlTermsAndConditions = createSelector(
    [getService],
    (service): string | undefined => service?.urlTermsAndConditions,
);

export const getDropoffId = (state: IAppState): string | null =>
    state.checkout.dropoffPointId ?? state.checkout?.thirdPartyPreferences?.dropoffPointId ?? null;

export const getQrSelected = (state: IAppState): boolean =>
    state.checkout.service ? state.checkout.service.printInStore : false;

export const getPackages = (state: IAppState): IParcel | undefined => state.checkout.packages;

export const getInPostServiceSelected = (state: IAppState): boolean =>
    state.checkout.service?.inPostServiceSelected ?? false;

export const getServicesTabSelected = (state: IAppState): ServicesType => state.checkout.servicesTabSelected;

export const getTransactionInfo = (state: IAppState): ITransactionInfo => ({
    id: state.checkout.customReference,
    date: state.checkout.orderDate,
    totalItems: state.checkout.items.length,
    orderTotal: state.checkout.orderTotal,
    currency: state.checkout.currency,
});

export const getCustomsShipmentDetails = createSelector(
    [getPackages, getPriceDetails],
    (parcel: IParcel | undefined, priceInfo: IPrice | undefined): ICustomsShipmentDetails => {
        return {
            cost: priceInfo?.price?.totalPrice || 0,
            parcelWeight: parcel?.weight || 0,
            parcelCount: 1,
        };
    },
);

export const getShipmentWeight = createSelector([getPackages], (parcel?: IParcel) => parcel?.weight ?? 0);

export const getCustomsInvoiceExternalData = createSelector(
    [getFromAddress, getToAddress, getCustomsShipmentDetails],
    (from, to, shipmentDetails): ICustomsInvoiceExternalData => {
        const signatureFullName = `${from?.firstName} ${from?.lastName}`;
        const signatureCity = from?.city || '';

        return {
            sender: from,
            receiver: to,
            shipmentDetails,
            signature: {
                fullName: signatureFullName,
                city: signatureCity,
            },
        } as ICustomsInvoiceExternalData;
    },
);

export const getShipmentCurrency = (state: IAppState): string | null => state.checkout.currency;
export const getCurrency = (state: IAppState): string | undefined => state.checkout.service?.currency;
export const getPriceAmount = (state: IAppState): number | undefined => state.checkout.priceDetails?.price?.totalPrice;

export const getSelectedPickUpPointId = (state: IAppState): string | null => state.checkout.selectedPickUpPointId;
export const getSelectedServiceId = (state: IAppState): number | null => state.checkout.selectedServiceId;
export const getContentValue = (state: IAppState): number | undefined => state.checkout.content?.value;
export const getThirdPartyPreferences = (state: IAppState): ShipmentThirdPartyPreferencesProps | undefined =>
    state.checkout.thirdPartyPreferences;

export const getShipmentItems = createSelector([(state: IAppState) => state.checkout.items], (items) => items);
export const getClientInputMode = createSelector(
    [(state: IAppState) => state.checkout.clientInputMode],
    (clientInputMode) => clientInputMode,
);

export const getIsGBShipment = createSelector(
    [getFromAddress, getToAddress],
    (from, to) => UK_ISO_CODES.includes(from?.alpha2Code || '') && UK_ISO_CODES.includes(to?.alpha2Code || ''),
);

const getShippingServiceCost = (state: IAppState): number => state.checkout.shippingServiceCost;
const getShippingServiceCurrency = (state: IAppState): string | undefined => state.checkout.shippingServiceCurrency;
const getShippingServiceName = (state: IAppState): string | null => state.checkout.shippingServiceName;
export const getShippingService = createSelector(
    [getShippingServiceCost, getShippingServiceCurrency, getShippingServiceName],
    (shippingServiceCost, shippingServiceCurrency, shippingServiceName) => ({
        shippingServiceCost,
        shippingServiceCurrency,
        shippingServiceName,
    }),
);
